import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, UserRole, AuthContextType, Organization } from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch organization details
  const fetchOrganization = useCallback(async (organizationId: string) => {
    try {
      const response = await fetch(`${API_URL}/organizations/${organizationId}`);
      if (response.ok) {
        const orgData = await response.json();
        setOrganization(orgData);
        localStorage.setItem('organization', JSON.stringify(orgData));
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    }
  }, []);

  const refreshOrganization = useCallback(async () => {
    if (user?.organizationId) {
      await fetchOrganization(user.organizationId);
    }
  }, [user?.organizationId, fetchOrganization]);

  useEffect(() => {
    // Check for existing session on mount
    const storedUser = localStorage.getItem('user');
    const storedOrg = localStorage.getItem('organization');
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Also restore organization
        if (storedOrg) {
          setOrganization(JSON.parse(storedOrg));
        } else if (parsedUser.organizationId) {
          // Fetch organization if we have the ID but no stored org
          fetchOrganization(parsedUser.organizationId);
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('organization');
      }
    }
    setIsLoading(false);
  }, [fetchOrganization]);

  const login = async (userData: Omit<User, 'role'>) => {
    // For now, determine role based on email domain or allow manual selection
    // In production, you'd check against a database
    const role: UserRole = userData.email.includes('@admin.') || userData.email.includes('admin') 
      ? 'admin' 
      : 'employee';
    
    const newUser: User = {
      ...userData,
      role,
    };
    
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
    
    // Verify role and get organization info from backend
    try {
      const response = await fetch(`${API_URL}/auth/verify-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userData.email }),
      });
      
      if (response.ok) {
        const { role: backendRole, organizationId, isSuperAdmin } = await response.json();
        const updatedUser: User = { 
          ...newUser, 
          role: backendRole || role,
          organizationId,
          isSuperAdmin: isSuperAdmin || false,
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Fetch organization details if user belongs to one
        if (organizationId) {
          await fetchOrganization(organizationId);
        }
      }
    } catch (error) {
      console.error('Error verifying role with backend:', error);
      // Continue with default role
    }
  };

  const logout = () => {
    setUser(null);
    setOrganization(null);
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
  };

  const setUserRole = (role: UserRole) => {
    if (user) {
      const updatedUser = { ...user, role };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const handleSetOrganization = (org: Organization | null) => {
    setOrganization(org);
    if (org) {
      localStorage.setItem('organization', JSON.stringify(org));
    } else {
      localStorage.removeItem('organization');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        setUserRole,
        setOrganization: handleSetOrganization,
        refreshOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
